import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Link, Navigate } from 'react-router-dom';
import {
  Save,
  Loader2,
  Image as ImageIcon,
  Settings as SettingsIcon,
  Mail,
  ShieldCheck,
  BarChart3,
  CircleUserRound
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getBranding, updateBranding, BrandingData } from '../api/brandingApi';
import { getStripeConnection, StripeConnection } from '../api/stripeApi';
import { EmailIntegration } from '../components/EmailIntegration';
import { CompanyProfileTab } from '../components/settings/branding/CompanyProfileTab';
import { InvoiceTaxTab } from '../components/settings/branding/InvoiceTaxTab';
import { PaymentSettingsTab } from '../components/settings/branding/PaymentSettingsTab';
import { AdminSettings } from './settings/AdminSettings';
import { TalentDiagnosticsConfig } from '../components/talent-diagnostics/TalentDiagnosticsConfig';
import { useAuth } from '../context/AuthContext';
import { getSettingsTabs, isAdminUser } from '../utils/settingsAccess';
import { StripeConnectCallback } from './settings/StripeConnectCallback';
import { ProfileSettings } from '../components/settings/profile/ProfileSettings';

const BrandingSettings = () => {
  const [activeTab, setActiveTab] = useState<'company' | 'invoice' | 'payment'>('company');
  const [formData, setFormData] = useState<BrandingData>({
    companyName: '',
    address: '',
    contactNumber: '',
    logoUrl: '',
    termsOfService: '',
    about: '',
    emailAddress: '',
    taxAmount: 0
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stripeConnection, setStripeConnection] = useState<StripeConnection | null>(null);
  const [stripeLoading, setStripeLoading] = useState(true);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const [brandingData, stripeData] = await Promise.all([getBranding(), getStripeConnection()]);
        if (brandingData) setFormData(brandingData);
        setStripeConnection(stripeData);
      } catch (err) {
        console.error('Failed to fetch branding data:', err);
      } finally {
        setLoading(false);
        setStripeLoading(false);
      }
    };

    fetchBranding();
  }, []);

  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);

    if (file) {
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleFieldChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!formData.companyName || !formData.contactNumber) {
      toast.error('Company Name and Contact Number are required.');
      return;
    }

    setSaving(true);

    try {
      await updateBranding({ ...formData, logoFile: logoFile || undefined });
      toast.success('Branding settings saved successfully!');
    } catch {
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin mb-2 text-strivre-burgundy" />
        <p>Loading settings...</p>
      </div>
    );
  }

  const brandTabs = [
    { key: 'company', label: 'Company Profile' },
    { key: 'invoice', label: 'Invoice & Tax' },
    { key: 'payment', label: 'Payment Settings' },
  ] as const;

  return (
    <div className="w-full animate-fade-in">
      <div className="bg-white border border-strivre-lightnavy rounded-2xl overflow-hidden shadow-sm w-full">
        <div className="p-6 border-b border-slate-100 bg-slate-50/30">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-900 text-left">Branding Details</h3>
              <p className="text-sm text-slate-500">Configure how your company appears across the platform.</p>
            </div>
            <button
              type="button"
              onClick={() => handleSave()}
              disabled={saving}
              className="inline-flex items-center gap-2 self-start rounded-2xl bg-strivre-burgundy px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-red-900 disabled:bg-red-800"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
              {saving ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6 text-left">
          <div className="flex flex-wrap gap-2 rounded-2xl border border-strivre-lightnavy bg-strivre-softgrey p-2">
            {brandTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all ${activeTab === tab.key ? 'bg-strivre-burgundy text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'company' && (
            <CompanyProfileTab
              formData={formData}
              logoPreview={logoPreview}
              logoFile={logoFile}
              saving={saving}
              onFieldChange={handleFieldChange}
              onFileChange={handleFileChange}
              onSave={handleSave}
            />
          )}

          {activeTab === 'invoice' && <InvoiceTaxTab formData={formData} onFieldChange={handleFieldChange} />}

          {activeTab === 'payment' && (
            <PaymentSettingsTab connection={stripeConnection} loading={stripeLoading} onConnectionChange={setStripeConnection} />
          )}
        </div>
      </div>
    </div>
  );
};

const settingsIcons: Record<string, React.ReactNode> = {
  profile: <CircleUserRound size={18} />,
  branding: <ImageIcon size={18} />,
  email: <Mail size={18} />,
  admin: <ShieldCheck size={18} />,
  diagnostics: <BarChart3 size={18} />,
};

export default function Settings() {
  const location = useLocation();
  const { token } = useAuth();

  const tabs = getSettingsTabs(token);
  const isAdmin = isAdminUser(token);

  const isTabActive = (path: string) => {
    if (path === 'branding') {
      return (
        location.pathname === '/settings' ||
        location.pathname.startsWith('/settings/branding')
      );
    }

    return location.pathname.startsWith(`/settings/${path}`);
  };

  return (
    <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0">
      <header className="mt-5 mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
            <SettingsIcon className="text-strivre-burgundy" size={24} />
            Settings
          </h1>
          <p className="text-slate-500 text-sm mt-1 text-left">
            Manage your organization preferences and platform appearance.
          </p>
        </div>
      </header>

      <div className="flex flex-col space-y-6">
        <div className="w-full lg:w-auto overflow-visible">
          <div className="flex flex-wrap gap-1 p-1 md:p-1.5 bg-strivre-softgrey rounded-2xl md:rounded-[1.25rem] border border-strivre-lightnavy shadow-inner">
            {tabs.map((tab) => (
              <Link
                key={tab.path}
                to={`/settings/${tab.path}`}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs md:text-sm font-bold transition-all flex-1 lg:flex-none ${
                  isTabActive(tab.path)
                    ? 'bg-strivre-burgundy text-white shadow-sm'
                    : 'text-slate-500 hover:text-strivre-burgundy'
                }`}
              >
                {settingsIcons[tab.path] ?? <SettingsIcon size={18} />}
                {tab.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="pt-2">
          <Routes>
            <Route path="profile" element={<ProfileSettings />}/>
            <Route path="branding" element={<BrandingSettings />} />
            <Route path="email" element={<EmailIntegration />} />
            {isAdmin && <Route path="admin" element={<AdminSettings />} />}
            {isAdmin && <Route path="diagnostics" element={<TalentDiagnosticsConfig />} />}
            <Route path="branding/payment/callback" element={<StripeConnectCallback />} />
            <Route index element={<Navigate to="branding" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}